import React, { useState, useEffect } from 'react';
import { X, Mail, Clock, Plus, Trash2, CheckCircle, XCircle, Loader2, UserCheck, ShieldCheck, Building2, User, Calendar, Users, KeyRound, Settings, Calculator, Share2, Search, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface SharingOperation {
  id: string;
  name: string;
}

interface AdminPermissions {
  generate_invoice: boolean;
  generate_report: boolean;
  add_member: boolean;
  edit_member: boolean;
  delete_member: boolean;
  import_data: boolean;
  donnees_qh: boolean;
}

const DEFAULT_PERMISSIONS: AdminPermissions = {
  generate_invoice: true,
  generate_report: true,
  add_member: true,
  edit_member: true,
  delete_member: true,
  import_data: true,
  donnees_qh: true,
};

const PERMISSION_LABELS: { key: keyof AdminPermissions; label: string }[] = [
  { key: 'generate_invoice', label: 'Génération de facture' },
  { key: 'generate_report', label: 'Génération de rapport' },
  { key: 'add_member', label: 'Ajout de membre' },
  { key: 'edit_member', label: 'Modification des infos des membres' },
  { key: 'delete_member', label: 'Suppression de membre' },
  { key: 'import_data', label: "Import de données manuel" },
  { key: 'donnees_qh', label: 'Données quart-horaires (graphes + Google Sheet)' },
];

async function deleteLoginAccount(email: string) {
  const { data: { session } } = await supabase.auth.getSession();
  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user-account`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ userEmail: email }),
  });
  if (!response.ok) {
    const result = await response.json().catch(() => ({}));
    throw new Error(result.details || result.error || 'Suppression du compte impossible');
  }
}

interface OperationAdmin {
  id: string;
  email: string;
  invitation_sent_at: string;
  invitation_accepted_at: string | null;
  account_status?: string;
  sharing_operation_id?: string;
  permissions?: AdminPermissions;
}

interface SimulatorInvitation {
  id: string;
  email: string;
  duration_days: number;
  expires_at: string | null;
  accepted_at: string | null;
  validated_at: string | null;
  user_id: string | null;
  created_at: string;
}

interface SimulationRecord {
  id: string;
  name: string;
  status: string;
  created_at: string;
  created_by_email?: string | null;
}

interface SimulationShare {
  id: string;
  simulation_id: string;
  guest_email: string;
  created_at: string;
}

interface AccessManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  operations: SharingOperation[];
  isSuperAdmin: boolean;
}

type ActiveSection = 'managers' | 'simulator' | 'simulations' | 'reset';

export function AccessManagementModal({ isOpen, onClose, operations, isSuperAdmin }: AccessManagementModalProps) {
  const [activeSection, setActiveSection] = useState<ActiveSection>('managers');

  const [managers, setManagers] = useState<OperationAdmin[]>([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [selectedOperationId, setSelectedOperationId] = useState<string>('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermissions, setInvitePermissions] = useState<AdminPermissions>({ ...DEFAULT_PERMISSIONS });
  const [inviting, setInviting] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [editingPermissionsId, setEditingPermissionsId] = useState<string | null>(null);
  const [editingPermissions, setEditingPermissions] = useState<AdminPermissions>({ ...DEFAULT_PERMISSIONS });
  const [savingPermissions, setSavingPermissions] = useState(false);

  const [simInvitations, setSimInvitations] = useState<SimulatorInvitation[]>([]);
  const [loadingSim, setLoadingSim] = useState(false);
  const [simEmail, setSimEmail] = useState('');
  const [simStartDate, setSimStartDate] = useState('');
  const [simEndDate, setSimEndDate] = useState('');
  const [creatingSimInvite, setCreatingSimInvite] = useState(false);
  const [simActionId, setSimActionId] = useState<string | null>(null);

  const [allSimulations, setAllSimulations] = useState<SimulationRecord[]>([]);
  const [loadingSimulations, setLoadingSimulations] = useState(false);
  const [simulationShares, setSimulationShares] = useState<SimulationShare[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [selectedSimIds, setSelectedSimIds] = useState<Set<string>>(new Set());
  const [sharingSimulations, setSharingSimulations] = useState(false);
  const [simSearchQuery, setSimSearchQuery] = useState('');
  const [shareFilterEmail, setShareFilterEmail] = useState('');

  const [resetEmail, setResetEmail] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadManagers();
      loadSimInvitations();
      loadAllSimulations();
      loadSimulationShares();
      if (operations.length > 0 && !selectedOperationId) {
        setSelectedOperationId(operations[0].id);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && selectedOperationId) {
      loadManagers();
    }
  }, [selectedOperationId]);

  const loadManagers = async () => {
    setLoadingManagers(true);
    try {
      let query = supabase
        .from('sharing_operation_admins')
        .select('*, sharing_operation_id')
        .order('invitation_sent_at', { ascending: false });

      if (selectedOperationId) {
        query = query.eq('sharing_operation_id', selectedOperationId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setManagers(data || []);
    } catch {
      toast.error('Erreur lors du chargement des gestionnaires');
    } finally {
      setLoadingManagers(false);
    }
  };

  const loadSimInvitations = async () => {
    setLoadingSim(true);
    try {
      const { data, error } = await supabase
        .from('simulator_invitations')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSimInvitations(data || []);
    } catch {
      toast.error('Erreur lors du chargement des accès simulateur');
    } finally {
      setLoadingSim(false);
    }
  };

  const handleInviteManager = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !selectedOperationId) return;

    setInviting(true);
    const toastId = toast.loading("Envoi de l'invitation...");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-admin-invitation`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sharing_operation_id: selectedOperationId,
            admin_email: inviteEmail.trim(),
            permissions: invitePermissions,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur');
      toast.success('Invitation envoyée avec succès', { id: toastId });
      setInviteEmail('');
      setInvitePermissions({ ...DEFAULT_PERMISSIONS });
      loadManagers();
    } catch (err: any) {
      toast.error(`Erreur: ${err.message}`, { id: toastId });
    } finally {
      setInviting(false);
    }
  };

  const handleSavePermissions = async (adminId: string) => {
    setSavingPermissions(true);
    try {
      const { error } = await supabase
        .from('sharing_operation_admins')
        .update({ permissions: editingPermissions })
        .eq('id', adminId);
      if (error) throw error;
      toast.success('Permissions mises à jour');
      setEditingPermissionsId(null);
      loadManagers();
    } catch {
      toast.error('Erreur lors de la mise à jour des permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleValidateManager = async (admin: OperationAdmin) => {
    setActionId(admin.id);
    const toastId = toast.loading('Validation en cours...');
    try {
      const { error } = await supabase
        .from('sharing_operation_admins')
        .update({ account_status: 'approved' })
        .eq('id', admin.id);
      if (error) throw error;

      const op = operations.find(o => o.id === admin.sharing_operation_id);
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/account-validation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'send_approval_email',
          email: admin.email,
          type: 'manager',
          operation_name: op?.name,
        }),
      });

      toast.success(`Accès validé pour ${admin.email}`, { id: toastId });
      loadManagers();
    } catch (err: any) {
      toast.error('Erreur lors de la validation', { id: toastId });
    } finally {
      setActionId(null);
    }
  };

  const handleRejectManager = async (admin: OperationAdmin) => {
    if (!confirm(`Refuser l'accès de ${admin.email} et supprimer son compte ?`)) return;
    setActionId(admin.id);
    const toastId = toast.loading('Refus en cours...');
    try {
      await supabase.from('sharing_operation_admins').update({ account_status: 'rejected' }).eq('id', admin.id);
      await deleteLoginAccount(admin.email);
      toast.success(`Accès refusé pour ${admin.email}`, { id: toastId });
      loadManagers();
    } catch {
      toast.error('Erreur lors du refus', { id: toastId });
    } finally {
      setActionId(null);
    }
  };

  const handleDeleteManager = async (admin: OperationAdmin) => {
    if (!confirm('Supprimer cet administrateur ? Son compte utilisateur sera également supprimé.')) return;
    setActionId(admin.id);
    const toastId = toast.loading('Suppression en cours...');
    try {
      await supabase.from('sharing_operation_admins').delete().eq('id', admin.id);
      await deleteLoginAccount(admin.email);
      toast.success('Administrateur supprimé', { id: toastId });
      loadManagers();
    } catch {
      toast.error('Erreur lors de la suppression', { id: toastId });
    } finally {
      setActionId(null);
    }
  };

  const handleCreateSimInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!simEmail.trim() || !simStartDate || !simEndDate) return;

    const start = new Date(simStartDate);
    const end = new Date(simEndDate);
    if (end <= start) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    setCreatingSimInvite(true);
    const toastId = toast.loading("Envoi de l'invitation...");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-simulator-invitation`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: simEmail.toLowerCase().trim(),
            duration_days: days,
          }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur');

      toast.success(`Invitation envoyée à ${simEmail}`, { id: toastId });
      setSimEmail('');
      setSimStartDate('');
      setSimEndDate('');
      loadSimInvitations();
    } catch (err: any) {
      toast.error(err.message || "Erreur lors de l'envoi", { id: toastId });
    } finally {
      setCreatingSimInvite(false);
    }
  };

  const handleValidateSimInvite = async (inv: SimulatorInvitation) => {
    setSimActionId(inv.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: ok, error } = await supabase.rpc('validate_simulator_invitation', {
        p_email: inv.email,
        p_admin_id: user?.id,
      });
      if (error) throw error;
      if (!ok) throw new Error('Validation échouée');
      toast.success(`Accès validé pour ${inv.email}`);
      loadSimInvitations();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la validation');
    } finally {
      setSimActionId(null);
    }
  };

  const handleDeleteSimInvite = async (id: string) => {
    setSimActionId(id);
    try {
      const { error } = await supabase.from('simulator_invitations').delete().eq('id', id);
      if (error) throw error;
      setSimInvitations(prev => prev.filter(inv => inv.id !== id));
      toast.success('Accès supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setSimActionId(null);
    }
  };

  const loadAllSimulations = async () => {
    setLoadingSimulations(true);
    try {
      const { data, error } = await supabase
        .from('simulations')
        .select('id, name, status, created_at, created_by_email')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAllSimulations(data || []);
    } catch {
      toast.error('Erreur lors du chargement des simulations');
    } finally {
      setLoadingSimulations(false);
    }
  };

  const loadSimulationShares = async () => {
    setLoadingShares(true);
    try {
      const { data, error } = await supabase
        .from('simulation_shares')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSimulationShares(data || []);
    } catch {
      toast.error('Erreur lors du chargement des partages');
    } finally {
      setLoadingShares(false);
    }
  };

  const handleShareSimulations = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareEmail.trim() || selectedSimIds.size === 0) return;

    setSharingSimulations(true);
    const toastId = toast.loading('Partage en cours...');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: { session } } = await supabase.auth.getSession();
      const email = shareEmail.toLowerCase().trim();

      const existingForEmail = simulationShares
        .filter(s => s.guest_email === email)
        .map(s => s.simulation_id);

      const newSimIds = [...selectedSimIds].filter(id => !existingForEmail.includes(id));

      if (newSimIds.length === 0) {
        toast.error('Ces simulations sont déjà partagées avec cet email', { id: toastId });
        setSharingSimulations(false);
        return;
      }

      const rows = newSimIds.map(simId => ({
        simulation_id: simId,
        guest_email: email,
        shared_by: user?.id,
      }));

      const { error } = await supabase.from('simulation_shares').insert(rows);
      if (error) throw error;

      const existingInvitation = simInvitations.find(inv => inv.email === email);
      if (!existingInvitation) {
        try {
          await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-simulator-invitation`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session?.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email,
                duration_days: 30,
              }),
            }
          );
          loadSimInvitations();
        } catch {
          // invitation sending failed but shares were saved
        }
      }

      toast.success(`${newSimIds.length} simulation(s) partagée(s) avec ${email}`, { id: toastId });
      setShareEmail('');
      setSelectedSimIds(new Set());
      loadSimulationShares();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors du partage', { id: toastId });
    } finally {
      setSharingSimulations(false);
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    try {
      const { error } = await supabase.from('simulation_shares').delete().eq('id', shareId);
      if (error) throw error;
      setSimulationShares(prev => prev.filter(s => s.id !== shareId));
      toast.success('Partage supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleResetAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = resetEmail.trim().toLowerCase();
    if (!email) return;
    if (!confirm(`Supprimer toutes les traces du compte "${email}" ? Cette action est irréversible.`)) return;

    setResetting(true);
    const toastId = toast.loading('Réinitialisation en cours...');
    try {
      // Delete from sharing_operation_admins
      await supabase.from('sharing_operation_admins').delete().eq('email', email);

      // Delete from simulator_invitations
      await supabase.from('simulator_invitations').delete().eq('email', email);

      // Delete simulation_shares linked to this email
      await supabase.from('simulation_shares').delete().eq('guest_email', email);

      // Clear participant email so the person can re-register
      await supabase.from('participants').update({
        email: null,
        account_status: null,
        account_request_date: null,
      }).eq('email', email);

      await deleteLoginAccount(email);

      toast.success(`Compte "${email}" entièrement réinitialisé`, { id: toastId });
      setResetEmail('');
      loadManagers();
      loadSimInvitations();
      loadSimulationShares();
    } catch (err: any) {
      toast.error(`Erreur: ${err.message}`, { id: toastId });
    } finally {
      setResetting(false);
    }
  };

  const filteredSimulations = allSimulations.filter(sim => {
    if (!simSearchQuery) return true;
    const q = simSearchQuery.toLowerCase();
    return sim.name.toLowerCase().includes(q) || (sim.created_by_email || '').toLowerCase().includes(q);
  });

  const uniqueShareEmails = [...new Set(simulationShares.map(s => s.guest_email))];

  const filteredShares = shareFilterEmail
    ? simulationShares.filter(s => s.guest_email === shareFilterEmail)
    : simulationShares;

  const getSimStatus = (inv: SimulatorInvitation) => {
    if (!inv.user_id) return { label: "En attente d'inscription", color: 'bg-neutral-100 text-neutral-500' };
    if (!inv.validated_at) return { label: 'En attente de validation', color: 'bg-amber-100 text-amber-700' };
    if (!inv.expires_at) return { label: 'Actif', color: 'bg-emerald-100 text-emerald-700' };
    const now = new Date();
    const expires = new Date(inv.expires_at);
    if (expires < now) return { label: 'Expiré', color: 'bg-red-100 text-red-600' };
    const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return { label: `${daysLeft}j restants`, color: 'bg-emerald-100 text-emerald-700' };
  };

  const pendingManagerValidation = managers.filter(m => m.account_status === 'pending' && m.invitation_accepted_at);
  const otherManagers = managers.filter(m => !(m.account_status === 'pending' && m.invitation_accepted_at));
  const pendingSimValidation = simInvitations.filter(inv => inv.user_id && !inv.validated_at);
  const otherSimInvitations = simInvitations.filter(inv => !(inv.user_id && !inv.validated_at));

  const totalPending = pendingManagerValidation.length + pendingSimValidation.length;

  const today = new Date().toISOString().split('T')[0];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[88vh]">

        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F8BD3D] rounded-lg flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-neutral-900" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-neutral-900">Gérer les accès</h2>
              <p className="text-xs text-neutral-500">Gestionnaires de CE et accès simulateur</p>
            </div>
          </div>
          {totalPending > 0 && (
            <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full mr-2">
              {totalPending} en attente
            </span>
          )}
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="flex border-b border-neutral-100 shrink-0">
          <button
            onClick={() => setActiveSection('managers')}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeSection === 'managers' ? 'border-[#F8BD3D] text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
          >
            <Building2 className="w-4 h-4" />
            Gestionnaires CE
            {pendingManagerValidation.length > 0 && (
              <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{pendingManagerValidation.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveSection('simulator')}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeSection === 'simulator' ? 'border-[#F8BD3D] text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
          >
            <Users className="w-4 h-4" />
            Accès simulateur
            {pendingSimValidation.length > 0 && (
              <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{pendingSimValidation.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveSection('simulations')}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeSection === 'simulations' ? 'border-[#F8BD3D] text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
          >
            <Calculator className="w-4 h-4" />
            Simulations
          </button>
          <button
            onClick={() => setActiveSection('reset')}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${activeSection === 'reset' ? 'border-red-400 text-red-600' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
          >
            <AlertTriangle className="w-4 h-4" />
            Réinitialiser
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {activeSection === 'managers' && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-xs text-blue-700 font-medium mb-1">Comment ça fonctionne</p>
                <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Sélectionnez la CE et saisissez l'email du gestionnaire</li>
                  <li>Le gestionnaire crée son compte sur sunisup.be (section "Gestionnaire")</li>
                  <li>Validez le compte depuis cette page — l'accès est immédiatement activé</li>
                </ol>
              </div>

              <form onSubmit={handleInviteManager} className="space-y-3">
                <p className="text-sm font-medium text-neutral-800">Inviter un gestionnaire</p>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">Communauté énergétique</label>
                  <select
                    value={selectedOperationId}
                    onChange={e => setSelectedOperationId(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300 bg-white"
                    required
                  >
                    <option value="">Sélectionner une CE...</option>
                    {operations.map(op => (
                      <option key={op.id} value={op.id}>{op.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      placeholder="email@gestionnaire.com"
                      required
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={inviting || !selectedOperationId}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-neutral-900 transition-all hover:opacity-90 disabled:opacity-60 shrink-0"
                    style={{ backgroundColor: '#F8BD3D' }}
                  >
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Inviter
                  </button>
                </div>
                <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-3">
                  <p className="text-xs font-medium text-neutral-600 mb-2.5 flex items-center gap-1.5">
                    <Settings className="w-3.5 h-3.5" />
                    Permissions accordées
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {PERMISSION_LABELS.map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={invitePermissions[key]}
                          onChange={e => setInvitePermissions(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="w-3.5 h-3.5 rounded border-neutral-300 text-amber-500 focus:ring-amber-300 cursor-pointer"
                        />
                        <span className="text-xs text-neutral-600 group-hover:text-neutral-800 transition-colors">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </form>

              {pendingManagerValidation.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-sm font-medium text-neutral-800">En attente de validation</p>
                    <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{pendingManagerValidation.length}</span>
                  </div>
                  <div className="space-y-2">
                    {pendingManagerValidation.map(admin => {
                      const op = operations.find(o => o.id === admin.sharing_operation_id);
                      return (
                        <div key={admin.id} className="px-4 py-3 rounded-xl border-2 border-amber-200 bg-amber-50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                              <UserCheck className="w-3.5 h-3.5 text-amber-700" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-800 truncate">{admin.email}</p>
                              <p className="text-xs text-neutral-500">{op?.name || '—'} · Inscrit, validation requise</p>
                            </div>
                            <button
                              onClick={() => handleValidateManager(admin)}
                              disabled={actionId === admin.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors disabled:opacity-60"
                            >
                              {actionId === admin.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                              Valider
                            </button>
                            <button
                              onClick={() => handleRejectManager(admin)}
                              disabled={actionId === admin.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors disabled:opacity-60"
                            >
                              <XCircle className="w-3 h-3" />
                              Refuser
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-neutral-800">Tous les gestionnaires</p>
                  {selectedOperationId && (
                    <button
                      onClick={() => setSelectedOperationId('')}
                      className="text-xs text-amber-600 hover:text-amber-700 underline"
                    >
                      Voir toutes les CE
                    </button>
                  )}
                </div>
                {loadingManagers ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-neutral-300" />
                  </div>
                ) : otherManagers.length === 0 && pendingManagerValidation.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed border-neutral-200 rounded-xl">
                    <Building2 className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                    <p className="text-sm text-neutral-400">Aucun gestionnaire</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {otherManagers.map(admin => {
                      const op = operations.find(o => o.id === admin.sharing_operation_id);
                      const isApproved = admin.account_status === 'approved';
                      const isWaiting = !admin.invitation_accepted_at;
                      const isEditingThis = editingPermissionsId === admin.id;
                      const perms: AdminPermissions = admin.permissions ?? { ...DEFAULT_PERMISSIONS };
                      return (
                        <div key={admin.id} className="rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 transition-colors overflow-hidden">
                          <div className="flex items-center gap-3 px-4 py-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isApproved ? 'bg-emerald-100' : 'bg-neutral-100'}`}>
                              <User className={`w-3.5 h-3.5 ${isApproved ? 'text-emerald-600' : 'text-neutral-400'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-800 truncate">{admin.email}</p>
                              <p className="text-xs text-neutral-400">
                                {op?.name || '—'} ·{' '}
                                {isApproved
                                  ? `Actif depuis le ${new Date(admin.invitation_accepted_at!).toLocaleDateString('fr-BE')}`
                                  : isWaiting
                                  ? `Invitation envoyée le ${new Date(admin.invitation_sent_at).toLocaleDateString('fr-BE')}`
                                  : 'En attente'}
                              </p>
                            </div>
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${isApproved ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'}`}>
                              {isApproved ? 'Actif' : 'Invitation envoyée'}
                            </span>
                            <button
                              onClick={() => {
                                if (isEditingThis) {
                                  setEditingPermissionsId(null);
                                } else {
                                  setEditingPermissionsId(admin.id);
                                  setEditingPermissions({ ...perms });
                                }
                              }}
                              className={`p-1.5 rounded-lg transition-colors ${isEditingThis ? 'text-amber-600 bg-amber-50' : 'text-neutral-400 hover:text-amber-600 hover:bg-amber-50'}`}
                              title="Modifier les permissions"
                            >
                              <Settings className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteManager(admin)}
                              disabled={actionId === admin.id}
                              className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              {actionId === admin.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                          {isEditingThis && (
                            <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-3">
                              <p className="text-xs font-medium text-neutral-600 mb-2 flex items-center gap-1.5">
                                <Settings className="w-3 h-3" />
                                Permissions
                              </p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3">
                                {PERMISSION_LABELS.map(({ key, label }) => (
                                  <label key={key} className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      checked={editingPermissions[key]}
                                      onChange={e => setEditingPermissions(prev => ({ ...prev, [key]: e.target.checked }))}
                                      className="w-3.5 h-3.5 rounded border-neutral-300 text-amber-500 focus:ring-amber-300 cursor-pointer"
                                    />
                                    <span className="text-xs text-neutral-600 group-hover:text-neutral-800 transition-colors">{label}</span>
                                  </label>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSavePermissions(admin.id)}
                                  disabled={savingPermissions}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors disabled:opacity-60"
                                >
                                  {savingPermissions ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                                  Enregistrer
                                </button>
                                <button
                                  onClick={() => setEditingPermissionsId(null)}
                                  className="px-3 py-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                                >
                                  Annuler
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {activeSection === 'simulator' && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-xs text-blue-700 font-medium mb-1">Comment ça fonctionne</p>
                <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Ajoutez l'email et définissez la période d'accès (début → fin)</li>
                  <li>L'utilisateur crée son compte sur sunisup.be (section "Accès simulateur")</li>
                  <li>Validez le compte depuis cette page — l'accès est immédiatement activé</li>
                </ol>
              </div>

              <form onSubmit={handleCreateSimInvite} className="space-y-3">
                <p className="text-sm font-medium text-neutral-800">Autoriser un nouvel accès</p>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                  <input
                    type="email"
                    value={simEmail}
                    onChange={e => setSimEmail(e.target.value)}
                    placeholder="email@exemple.com"
                    required
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300"
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-neutral-500 mb-1.5">Date de début</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                      <input
                        type="date"
                        value={simStartDate}
                        min={today}
                        onChange={e => setSimStartDate(e.target.value)}
                        required
                        className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-neutral-500 mb-1.5">Date de fin</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                      <input
                        type="date"
                        value={simEndDate}
                        min={simStartDate || today}
                        onChange={e => setSimEndDate(e.target.value)}
                        required
                        className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300"
                      />
                    </div>
                  </div>
                </div>
                {simStartDate && simEndDate && new Date(simEndDate) > new Date(simStartDate) && (
                  <p className="text-xs text-neutral-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Durée : {Math.ceil((new Date(simEndDate).getTime() - new Date(simStartDate).getTime()) / (1000 * 60 * 60 * 24))} jour(s)
                  </p>
                )}
                <button
                  type="submit"
                  disabled={creatingSimInvite || !simEmail || !simStartDate || !simEndDate}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-neutral-900 transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: '#F8BD3D' }}
                >
                  {creatingSimInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Ajouter l'accès
                </button>
              </form>

              {pendingSimValidation.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-sm font-medium text-neutral-800">En attente de validation</p>
                    <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{pendingSimValidation.length}</span>
                  </div>
                  <div className="space-y-2">
                    {pendingSimValidation.map(inv => (
                      <div key={inv.id} className="px-4 py-3 rounded-xl border-2 border-amber-200 bg-amber-50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                            <UserCheck className="w-3.5 h-3.5 text-amber-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-neutral-800 truncate">{inv.email}</p>
                            <p className="text-xs text-neutral-500">Inscrit · {inv.duration_days} jours après validation</p>
                          </div>
                          <button
                            onClick={() => handleValidateSimInvite(inv)}
                            disabled={simActionId === inv.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors disabled:opacity-60"
                          >
                            {simActionId === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                            Valider
                          </button>
                          <button
                            onClick={() => handleDeleteSimInvite(inv.id)}
                            disabled={simActionId === inv.id}
                            className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-neutral-800 mb-3">Tous les accès simulateur</p>
                {loadingSim ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-neutral-300" />
                  </div>
                ) : otherSimInvitations.length === 0 && pendingSimValidation.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed border-neutral-200 rounded-xl">
                    <Users className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                    <p className="text-sm text-neutral-400">Aucun accès créé</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {otherSimInvitations.map(inv => {
                      const status = getSimStatus(inv);
                      return (
                        <div key={inv.id} className="px-4 py-3 rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center shrink-0">
                              <Mail className="w-3.5 h-3.5 text-neutral-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-800 truncate">{inv.email}</p>
                              <p className="text-xs text-neutral-400">
                                {inv.duration_days} jours
                                {inv.expires_at && ` · expire le ${new Date(inv.expires_at).toLocaleDateString('fr-BE')}`}
                              </p>
                            </div>
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${status.color}`}>
                              {status.label}
                            </span>
                            <button
                              onClick={() => handleDeleteSimInvite(inv.id)}
                              disabled={simActionId === inv.id}
                              className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                            >
                              {simActionId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {activeSection === 'simulations' && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-xs text-blue-700 font-medium mb-1">Comment ca fonctionne</p>
                <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Selectionnez une ou plusieurs simulations dans la liste ci-dessous</li>
                  <li>Saisissez l'email du destinataire et cliquez sur "Partager"</li>
                  <li>L'invité crée son compte, accède au simulateur et voit les résultats en lecture seule</li>
                </ol>
              </div>

              <form onSubmit={handleShareSimulations} className="space-y-3">
                <p className="text-sm font-medium text-neutral-800">Partager des simulations</p>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                  <input
                    type="text"
                    value={simSearchQuery}
                    onChange={e => setSimSearchQuery(e.target.value)}
                    placeholder="Rechercher une simulation..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300"
                  />
                </div>

                <div className="border border-neutral-200 rounded-xl max-h-48 overflow-y-auto">
                  {loadingSimulations ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-neutral-300" />
                    </div>
                  ) : filteredSimulations.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-xs text-neutral-400">Aucune simulation trouvée</p>
                    </div>
                  ) : (
                    filteredSimulations.map(sim => {
                      const isSelected = selectedSimIds.has(sim.id);
                      return (
                        <label
                          key={sim.id}
                          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-neutral-50 transition-colors border-b border-neutral-100 last:border-b-0 ${isSelected ? 'bg-amber-50' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setSelectedSimIds(prev => {
                                const next = new Set(prev);
                                if (next.has(sim.id)) next.delete(sim.id);
                                else next.add(sim.id);
                                return next;
                              });
                            }}
                            className="w-3.5 h-3.5 rounded border-neutral-300 text-amber-500 focus:ring-amber-300"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-neutral-800 truncate">{sim.name || 'Sans nom'}</p>
                            <p className="text-xs text-neutral-400">
                              {new Date(sim.created_at).toLocaleDateString('fr-BE')}
                              {sim.created_by_email && ` · ${sim.created_by_email}`}
                            </p>
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${sim.status === 'complete' ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'}`}>
                            {sim.status === 'complete' ? 'Terminée' : 'Brouillon'}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                {selectedSimIds.size > 0 && (
                  <p className="text-xs text-amber-600 font-medium">{selectedSimIds.size} simulation(s) sélectionnée(s)</p>
                )}

                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={e => setShareEmail(e.target.value)}
                      placeholder="email@destinataire.com"
                      required
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300/50 focus:border-amber-300"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sharingSimulations || selectedSimIds.size === 0 || !shareEmail.trim()}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-neutral-900 transition-all hover:opacity-90 disabled:opacity-60 shrink-0"
                    style={{ backgroundColor: '#F8BD3D' }}
                  >
                    {sharingSimulations ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    Partager
                  </button>
                </div>
              </form>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-neutral-800">Simulations partagées</p>
                  {uniqueShareEmails.length > 1 && (
                    <select
                      value={shareFilterEmail}
                      onChange={e => setShareFilterEmail(e.target.value)}
                      className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                    >
                      <option value="">Tous les emails</option>
                      {uniqueShareEmails.map(email => (
                        <option key={email} value={email}>{email}</option>
                      ))}
                    </select>
                  )}
                </div>
                {loadingShares ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-neutral-300" />
                  </div>
                ) : filteredShares.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed border-neutral-200 rounded-xl">
                    <Share2 className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                    <p className="text-sm text-neutral-400">Aucune simulation partagée</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredShares.map(share => {
                      const sim = allSimulations.find(s => s.id === share.simulation_id);
                      return (
                        <div key={share.id} className="px-4 py-3 rounded-xl border border-neutral-200 bg-white hover:border-neutral-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                              <Calculator className="w-3.5 h-3.5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-800 truncate">{sim?.name || 'Simulation supprimée'}</p>
                              <p className="text-xs text-neutral-400 truncate">
                                Partagée avec {share.guest_email} · {new Date(share.created_at).toLocaleDateString('fr-BE')}
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveShare(share.id)}
                              className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {activeSection === 'reset' && (
            <>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-xs text-red-700 font-medium mb-1">Attention — action irréversible</p>
                <p className="text-xs text-red-700">
                  Supprime le compte utilisateur, les accès gestionnaire CE, les accès simulateur et les partages de simulations liés à cet email.
                  La personne pourra ensuite se réinscrire sans blocage.
                </p>
              </div>

              <form onSubmit={handleResetAccount} className="space-y-3">
                <p className="text-sm font-medium text-neutral-800">Email du compte à réinitialiser</p>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={e => setResetEmail(e.target.value)}
                      placeholder="email@exemple.com"
                      required
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-300/50 focus:border-red-300"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={resetting || !resetEmail.trim()}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white bg-red-500 hover:bg-red-600 transition-all disabled:opacity-60 shrink-0"
                  >
                    {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Supprimer
                  </button>
                </div>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
